import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import {
  Search,
  Clock,
  X,
  ChevronRight,
  CornerDownLeft,
  CornerUpRight,
  FileText,
  Zap,
  Activity,
  Star,
  AlertTriangle,
  SearchX,
  Sparkles,
  TrendingUp,
  BarChart3,
  CloudSun,
  MapPin,
  Calendar,
} from "lucide-react";
import { useJvvnlSearch } from "./useJvvnlSearch";
import { useSearchDiscovery } from "./useSearchDiscovery";
import SearchDiscoveryTooltip from "./SearchDiscoveryTooltip";
import { highlightMatch, RankedItem } from "./searchUtils";
import { WEATHER_PARAM_LABEL } from "./deepLink";
import { SearchItem, SearchTypeFilter } from "./types";
import "./GlobalSearch.css";

const FILTERS: { key: SearchTypeFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "page", label: "Pages" },
  { key: "entity", label: "Entities" },
  { key: "metric", label: "Metrics" },
];

function typeIcon(type: SearchItem["type"], category?: string) {
  if (type === "action") return <CornerUpRight size={15} />;
  if (type === "page") return <FileText size={15} />;
  if (category === "Weather") return <CloudSun size={15} />;
  if (type === "entity") return <Zap size={15} />;
  return <Activity size={15} />;
}

function HighlightedLabel({ text, query }: { text: string; query: string }) {
  // highlightMatch re-runs expandQuery; memoize so re-renders of unrelated rows
  // (e.g. hover state changes) don't recompute every label.
  const parts = useMemo(() => highlightMatch(text, query), [text, query]);
  return (
    <>
      {parts.map((p, i) =>
        p.hit ? (
          <span className="hit" key={i}>
            {p.text}
          </span>
        ) : (
          <React.Fragment key={i}>{p.text}</React.Fragment>
        ),
      )}
    </>
  );
}

// Icon per quick-search kind.
function quickIcon(
  kind: "weather" | "forecast" | "entity" | "metric" | "ranking",
) {
  if (kind === "weather") return <CloudSun size={12} />;
  if (kind === "forecast") return <TrendingUp size={12} />;
  if (kind === "entity") return <Zap size={12} />;
  if (kind === "ranking") return <Activity size={12} />;
  return <BarChart3 size={12} />;
}

// Rotating example searches for the input placeholder — showcases what the bar
// can do (entities, weather pairing, forecast, metrics, and the natural-language
// command phrases: actions, rankings, location, compare).
const PLACEHOLDER_EXAMPLES = [
  "Try “Badawas temperature tomorrow”",
  "Try “forecast for paota next week”",
  "Search a feeder, plant or circle…",
  "Try “Badawas rain on 25 june”",
  "Try “top feeders” or “highest load”",
  "Try “open forecast” or “load map”",
  "Try “KOTPUTLI humidity this month”",
  "Try “132kv substations”",
  "Try “Badawas load friday”",
  "Try “peak load” or “data quality”",
];

/**
 * Typewriter placeholder: types out an example, holds, deletes, advances to the
 * next. Pauses while the user has typed something (only runs on empty query).
 */
function useTypewriterPlaceholder(active: boolean) {
  const [text, setText] = useState("");
  const idxRef = useRef(0);
  const charRef = useRef(0);
  const phaseRef = useRef<"typing" | "holding" | "deleting">("typing");

  useEffect(() => {
    if (!active) {
      setText("");
      idxRef.current = 0;
      charRef.current = 0;
      phaseRef.current = "typing";
      return;
    }
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      const full = PLACEHOLDER_EXAMPLES[idxRef.current];
      const phase = phaseRef.current;
      if (phase === "typing") {
        charRef.current += 1;
        setText(full.slice(0, charRef.current));
        if (charRef.current >= full.length) {
          phaseRef.current = "holding";
          timer = setTimeout(tick, 1600);
          return;
        }
        timer = setTimeout(tick, 55);
      } else if (phase === "holding") {
        phaseRef.current = "deleting";
        timer = setTimeout(tick, 35);
      } else {
        charRef.current -= 1;
        setText(full.slice(0, Math.max(0, charRef.current)));
        if (charRef.current <= 0) {
          phaseRef.current = "typing";
          idxRef.current = (idxRef.current + 1) % PLACEHOLDER_EXAMPLES.length;
          timer = setTimeout(tick, 250);
          return;
        }
        timer = setTimeout(tick, 28);
      }
    };
    timer = setTimeout(tick, 400);
    return () => clearTimeout(timer);
  }, [active]);

  // `active` lets the caller keep showing the animated span (with cursor) during
  // the brief empty moment between examples, instead of flickering back to the
  // static placeholder.
  return { text, active };
}

export default function JvvnlGlobalSearch() {
  const [isOpen, setIsOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const s = useJvvnlSearch(isOpen);
  const location = useLocation();
  const discovery = useSearchDiscovery();

  // Force-close on ANY URL change. A result that navigates within the same route
  // (e.g. Metric → Metric with a new query string) does not unmount this
  // component, and ordering close() around navigate() proved unreliable; reacting
  // to the location change guarantees the modal dismisses once navigation lands.
  const lastLocRef = useRef(location.pathname + location.search);
  useEffect(() => {
    const key = location.pathname + location.search;
    if (key !== lastLocRef.current) {
      lastLocRef.current = key;
      if (isOpen) {
        setIsOpen(false);
        s.setQuery("");
      }
    }
  }, [location.pathname, location.search, isOpen, s]);

  // Animated placeholder — only while open and the user hasn't typed anything.
  const typedPh = useTypewriterPlaceholder(isOpen && !s.query);
  // Animated text for the compact header trigger (runs while the modal is closed).
  const triggerPh = useTypewriterPlaceholder(!isOpen);

  const open = () => {
    if (!isOpen)
      restoreFocusRef.current = document.activeElement as HTMLElement;
    setIsOpen(true);
    setBannerDismissed(false);
    discovery.complete(); // any open (click / Ctrl+K) permanently completes onboarding
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleTryExample = (query: string) => {
    s.setQuery(query);
    open(); // also completes onboarding
  };

  const close = () => {
    setIsOpen(false);
    s.setQuery("");
    restoreFocusRef.current?.focus?.();
  };

  // Cmd+K / Ctrl+K global shortcut.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        isOpen ? close() : open();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  // Outside-click while the discovery tooltip is shown → hide (non-completing).
  // Clicking the trigger itself opens the modal (which completes), so ignore it;
  // ignore clicks inside the tooltip too.
  const { visible: discoveryVisible, suppressForSession: discoverySuppress } =
    discovery;
  useEffect(() => {
    if (!discoveryVisible) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target?.closest(".search-discovery-tooltip")) return;
      if (triggerRef.current?.contains(target as Node)) return;
      discoverySuppress(); // click-outside → suppress rest of session
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [discoveryVisible, discoverySuppress]);

  // Lock body scroll while the modal is open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const handleSelect = (item: SearchItem, targetKind?: "Chart" | "Metric") => {
    const { navigated } = s.select(
      item,
      targetKind ?? (item.hasDualViews ? s.activeViewKind : undefined),
    );
    // When the URL actually changes, the close-on-location-change effect dismisses
    // the modal (ordering close() around navigate() proved unreliable — §16.5).
    // But re-picking the already-applied filter is a no-op navigation: location
    // never changes, that effect never fires, so close the modal explicitly here.
    if (!navigated) close();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const r = s.onKeyDown(e);
    if (r.close) close();
  };

  const showRecents = !s.hasQuery;
  // Empty state only when there is genuinely nothing to show — predictive
  // suggestions (prefix/substring hits) count as results, so a 2–4 char query
  // that prefix-matches anything never shows "No results found".
  const noResults =
    s.hasQuery &&
    s.flat.length === 0 &&
    s.groups.length === 0 &&
    s.predictiveSuggestions.length === 0;

  // Single place that wires a result item to all its handlers, so the favorites,
  // inline-list, and group sections render identically (no copy-paste drift).
  const renderRow = (
    item: SearchItem,
    opts?: { favSection?: boolean; suggestSection?: boolean },
  ) => (
    <ResultRow
      key={
        opts?.favSection
          ? `fav-${item.id}`
          : opts?.suggestSection
            ? `sugg-row-${item.id}`
            : item.id
      }
      item={item}
      query={opts?.favSection ? "" : s.query}
      active={!opts?.suggestSection && item.id === s.activeId}
      activeViewKind={s.activeViewKind}
      isFav={opts?.favSection ? true : s.isFav(item.id)}
      onToggleFav={() => s.toggleFav(item)}
      onSelect={() => handleSelect(item)}
      onSelectChart={() => handleSelect(item, "Chart")}
      onSelectMetric={() => handleSelect(item, "Metric")}
      onHover={() => s.setActiveById(item.id)}
      dateLabel={opts?.favSection ? null : (s.dateSlot?.label ?? null)}
    />
  );

  return (
    <>
      {/* Compact header trigger (wrapped so the discovery tooltip can anchor) */}
      <div className="jgs-trigger-wrap">
        <button
          ref={triggerRef}
          className={`jgs-trigger${discovery.visible ? " jgs-trigger--onboarding" : ""}`}
          onClick={open}
          onMouseEnter={
            discovery.eligible || discovery.visible
              ? () => {
                  discovery.pointerEnter(); // cancel any pending leave-hide
                  discovery.arm(); // 700ms dwell → show (no-op if not eligible)
                }
              : undefined
          }
          onMouseLeave={
            discovery.eligible || discovery.visible
              ? () => {
                  discovery.cancelArm(); // cancel not-yet-shown dwell
                  discovery.pointerLeave(); // start 2s leave-hide if shown
                }
              : undefined
          }
          onFocus={discovery.eligible ? discovery.arm : undefined}
          aria-label="Search"
        >
          <Search size={16} />
          <span className="jgs-trigger-text">
            {triggerPh.active
              ? `${triggerPh.text}▏`
              : "Search pages, feeders, plants…"}
          </span>
          <span className="jgs-kbd">⌘K</span>
        </button>
        {discovery.visible && (
          <SearchDiscoveryTooltip
            anchorRef={triggerRef}
            variant={discovery.variant}
            onTryExample={handleTryExample}
            onComplete={discovery.complete}
            onSuppress={discovery.suppressForSession}
            onHideSoft={discovery.hideSoft}
            onPointerEnter={discovery.pointerEnter}
            onPointerLeave={discovery.pointerLeave}
          />
        )}
      </div>

      {isOpen &&
        createPortal(
          <div
            className="jgs-overlay"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) close();
            }}
          >
            <div
              className="jgs-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Search JVVNL"
            >
              {/* Header / input */}
              <div className="jgs-modal-head">
                <div className="jgs-modal-title">Search JVVNL</div>
                <button
                  className="jgs-modal-close"
                  onClick={close}
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="jgs-modal-field">
                <Search size={18} />
                <div className="jgs-input-wrap">
                  {/* Inline ghost-text completion (Tab / → to accept) */}
                  {s.completion && (
                    <div className="jgs-ghost" aria-hidden="true">
                      <span className="jgs-ghost-typed">{s.query}</span>
                      <span className="jgs-ghost-rest">{s.completion}</span>
                    </div>
                  )}
                  <input
                    ref={inputRef}
                    className="jgs-modal-input"
                    placeholder={
                      typedPh.active
                        ? `${typedPh.text}▏`
                        : "Search pages, feeders, plants, metrics…"
                    }
                    value={s.query}
                    onChange={(e) => s.setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                {s.completion && <span className="jgs-ghost-hint">Tab ↹</span>}
                {s.query && (
                  <button
                    className="jgs-input-clear"
                    onClick={() => { s.setQuery(""); inputRef.current?.focus(); }}
                    aria-label="Clear search"
                  >
                    Clear
                  </button>
                )}
              </div>

              {s.status === "loading" && <div className="jgs-loading-bar" />}

              {/* Degraded-index warning banner */}
              {s.degraded && !bannerDismissed && (
                <div className="jgs-banner" role="alert">
                  <AlertTriangle size={15} />
                  <span>
                    Some search data could not be loaded. Showing limited
                    results.
                  </span>
                  <button
                    className="jgs-banner-x"
                    onClick={() => setBannerDismissed(true)}
                    aria-label="Dismiss"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Filter chips bar */}
              <div className="jgs-chips-wrapper">
                <div className="jgs-chips-left">
                  {FILTERS.map((f) => {
                    const isActive = s.typeFilter === f.key;
                    const count =
                      f.key === "all"
                        ? s.counts.all
                        : f.key === "page"
                          ? s.counts.page
                          : f.key === "entity"
                            ? s.counts.entity
                            : s.counts.metric;
                    return (
                      <button
                        key={f.key}
                        className={`jgs-chip ${isActive ? "is-active" : ""}`}
                        onClick={() => s.setTypeFilter(f.key)}
                      >
                        <span>{f.label}</span>
                        {s.hasQuery && count > 0 && (
                          <span className="jgs-chip-count">{count}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <button
                  className={`jgs-chip-favorite ${s.typeFilter === "favorite" ? "is-active" : ""}`}
                  onClick={() =>
                    s.setTypeFilter(
                      s.typeFilter === "favorite" ? "all" : "favorite",
                    )
                  }
                >
                  <Star
                    size={13}
                    fill={s.typeFilter === "favorite" ? "currentColor" : "none"}
                  />
                  <span>Favorites</span>
                  {s.counts.favorite > 0 && (
                    <span className="jgs-chip-count">{s.counts.favorite}</span>
                  )}
                </button>
              </div>

              {/* Body */}
              {showRecents ? (
                <div className="jgs-body jgs-recents">
                  {/* Quick searches — clickable example queries from the index */}
                  {s.quickSearches.length > 0 && (
                    <>
                      <div className="jgs-section-label">Quick Searches</div>
                      <div className="jgs-quick-searches">
                        {s.quickSearches.map((q) => (
                          <button
                            key={q.query}
                            className="jgs-quick-pill"
                            onClick={() => s.setQuery(q.query)}
                          >
                            {quickIcon(q.kind)}
                            <span>{q.label}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Favorites section in empty query */}
                  {s.favorites.length > 0 && (
                    <div className="jgs-fav-section">
                      <div className="jgs-section-label">Favorites</div>
                      {s.favorites.map((item) =>
                        renderRow(item, { favSection: true }),
                      )}
                    </div>
                  )}

                  <div className="jgs-section-label">Recent Searches</div>
                  {s.recents.length === 0 ? (
                    <div className="jgs-empty-inline">
                      Type to search across JVVNL — pages, feeders, plants,
                      metrics.
                    </div>
                  ) : (
                    s.recents.map((r) => (
                      <div
                        key={r.query}
                        className="jgs-recent-row"
                        onClick={() => s.setQuery(r.query)}
                      >
                        <Clock size={14} className="clock" />
                        <span>{r.query}</span>
                        <button
                          className="jgs-recent-x"
                          onClick={(e) => {
                            e.stopPropagation();
                            s.removeRecent(r.query);
                          }}
                          aria-label="Remove"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              ) : noResults ? (
                s.status === "loading" ? null : (
                  <div className="jgs-body">
                    <div className="jgs-empty-state" role="status">
                      <div className="jgs-empty-icon">
                        <SearchX size={22} />
                      </div>
                      <div className="jgs-empty-title">No results found</div>
                      <div className="jgs-empty-desc">
                        We couldn't find any matches for "{s.query}".
                      </div>

                      {s.typeFilter !== "all" && (
                        <button
                          className="jgs-empty-reset-btn"
                          onClick={() => s.setTypeFilter("all")}
                          title="Clear active category filters"
                        >
                          <X size={13} />
                          <span>
                            Clear "
                            {s.typeFilter === "page"
                              ? "Pages"
                              : s.typeFilter === "entity"
                                ? "Entities"
                                : s.typeFilter === "metric"
                                  ? "Metrics"
                                  : s.typeFilter === "favorite"
                                    ? "Favorites"
                                    : s.typeFilter}
                            " filter
                          </span>
                        </button>
                      )}

                      <div className="jgs-empty-tips">
                        <div className="jgs-empty-tip-item">
                          <Search size={13} className="icon-blue" />
                          <span>Try a broader keyword</span>
                        </div>
                        <div className="jgs-empty-tip-item">
                          <Zap size={13} className="icon-green" />
                          <span>Search feeder, plant, circle...</span>
                        </div>
                        <div className="jgs-empty-tip-item">
                          <Sparkles size={13} className="icon-purple" />
                          <span>Check spelling & shortcuts</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <div className="jgs-body jgs-list" role="listbox">
                  {/* Action Mode hint: confirms the system recognized command intent */}
                  {s.actionMode && (
                    <div className="jgs-action-hint">
                      <Zap size={12} />
                      <span>Actions</span>
                    </div>
                  )}
                  {/* Date suggestion strip: clickable date labels to append */}
                  {s.dateSuggestions.length > 0 && (
                    <div className="jgs-suggestions jgs-suggestions-compact jgs-date-suggest">
                      <Calendar size={12} className="jgs-sugg-icon" />
                      <span className="jgs-suggestions-label">Date:</span>
                      {s.dateSuggestions.map((d: string) => (
                        <button
                          type="button"
                          key={d}
                          className="jgs-sugg-pill jgs-date-pill"
                          onClick={() => s.appendDate(d)}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Modifier pills: append a weather word or "forecast" to the query */}
                  {(s.weatherPills.length > 0 || s.showForecastPill) && (
                    <div className="jgs-suggestions jgs-suggestions-compact jgs-weather-pills">
                      <Sparkles size={12} className="jgs-sugg-icon" />
                      <span className="jgs-suggestions-label">Add:</span>
                      {s.showForecastPill && (
                        <button
                          className="jgs-sugg-pill jgs-forecast-pill"
                          onClick={() => s.appendForecast()}
                        >
                          <TrendingUp size={11} />
                          <span>Forecast</span>
                        </button>
                      )}
                      {s.weatherPills.map((w) => (
                        <button
                          key={`wx-${w}`}
                          className="jgs-sugg-pill jgs-weather-pill"
                          onClick={() => s.appendWeather(w)}
                        >
                          <span style={{ textTransform: "capitalize" }}>
                            {w}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Suggestions pill strip */}
                  {s.suggestions.length > 0 && (
                    <div className="jgs-suggestions">
                      <Sparkles size={12} className="jgs-sugg-icon" />
                      <span className="jgs-suggestions-label">
                        Suggestions:
                      </span>
                      {s.suggestions.map((sg) => (
                        <button
                          key={`sugg-${sg.id}`}
                          className="jgs-sugg-pill"
                          onClick={() => handleSelect(sg)}
                          onMouseEnter={() => s.setActiveById(sg.id)}
                        >
                          <Search size={10} />
                          <span>{sg.label}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {s.flat.map((item) => renderRow(item))}

                  {s.groups.map((g) => {
                    const isOpenGroup = s.expanded.has(g.category);
                    return (
                      <div key={g.category} className="jgs-group-container">
                        <button
                          className={`jgs-group-toggle ${isOpenGroup ? "open" : ""}`}
                          onClick={() => s.toggleGroup(g.category)}
                        >
                          <ChevronRight size={15} className="chev" />
                          <span>
                            {isOpenGroup ? "Hide" : "Show"} more {g.category}
                          </span>
                          <span className="jgs-group-count">
                            {g.items.length}
                          </span>
                        </button>
                        {isOpenGroup && g.items.map((item) => renderRow(item))}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Keyboard shortcuts footer */}
              <div className="jgs-modal-foot">
                <span>
                  <kbd>↑</kbd>
                  <kbd>↓</kbd> Navigate rows
                </span>
                <span>
                  <kbd>←</kbd>
                  <kbd>→</kbd> / <kbd>Tab</kbd> Toggle View target
                </span>
                <span>
                  <kbd>↵</kbd> Select
                </span>
                <span>
                  <Star size={11} className="jgs-foot-star" /> Favorite
                </span>
                <span>
                  <kbd>Esc</kbd> Close
                </span>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

interface ResultRowProps {
  item: RankedItem;
  query: string;
  active: boolean;
  activeViewKind?: "Chart" | "Metric";
  isFav?: boolean;
  onToggleFav?: () => void;
  onSelect: () => void;
  onSelectChart?: () => void;
  onSelectMetric?: () => void;
  onHover: () => void;
  /** Active query's parsed date label; shown on the highlighted row only. */
  dateLabel?: string | null;
}

function ResultRow({
  item,
  query,
  active,
  activeViewKind,
  isFav,
  onToggleFav,
  onSelect,
  onSelectChart,
  onSelectMetric,
  onHover,
  dateLabel,
}: ResultRowProps) {
  const hasDual = item.hasDualViews;

  return (
    <div
      className={`jgs-item type-${item.type} ${active ? "is-active" : ""} ${
        hasDual ? "has-dual" : ""
      } ${item.disabled ? "is-disabled" : ""}`}
      onClick={item.disabled ? undefined : onSelect}
      onMouseEnter={onHover}
      role="option"
      aria-selected={active}
    >
      <div className="jgs-icon-wrapper">
        <span className="jgs-item-icon">
          {typeIcon(item.type, item.category)}
        </span>
        {onToggleFav && (
          <button
            className={`jgs-item-fav ${isFav ? "is-fav" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFav();
            }}
            aria-label={isFav ? "Remove favorite" : "Add favorite"}
            title={isFav ? "Remove favorite" : "Add favorite"}
          >
            <Star size={9} fill={isFav ? "currentColor" : "none"} />
          </button>
        )}
      </div>

      <div className="jgs-item-body">
        <div className="jgs-item-label">
          <HighlightedLabel text={item.label} query={query} />
        </div>

        {/* Hierarchy breadcrumb — the primary scannable line, directly under the name */}
        {item.mapping && (
          <div className="jgs-item-hierarchy">
            <MapPin size={11} className="jgs-pin-icon" />
            {item.mapping.split(" > ").map((seg, i, arr) => (
              <React.Fragment key={i}>
                <span className="jgs-crumb">
                  <HighlightedLabel text={seg} query={query} />
                </span>
                {i < arr.length - 1 && <span className="jgs-crumb-sep">›</span>}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Tertiary metadata: entity type + any attached modifiers */}
        <div className="jgs-item-meta">
          {item.hint && (
            <span className="jgs-item-tag">
              <HighlightedLabel text={item.hint} query={query} />
            </span>
          )}
          {item.badge && (
            <span className="jgs-item-badge-soon">{item.badge}</span>
          )}
          {item.weatherParam && (
            <span className="jgs-item-weather">
              <CloudSun size={11} />
              <HighlightedLabel
                text={WEATHER_PARAM_LABEL[item.weatherParam] ?? "Weather"}
                query={query}
              />
            </span>
          )}
          {item.forecast && (
            <span className="jgs-item-forecast">
              <TrendingUp size={11} />
              <HighlightedLabel text="Forecast" query={query} />
            </span>
          )}
          {/* Date applies to the whole query, not the row — show it on every row
              just like the weather pill. */}
          {dateLabel && (
            <span className="jgs-item-date">
              <Calendar size={11} />
              <span>{dateLabel}</span>
            </span>
          )}
        </div>
      </div>

      <div className="jgs-item-right">
        {hasDual ? (
          <div
            className="jgs-dual-buttons"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={`jgs-btn-view view-chart ${
                active && activeViewKind === "Chart"
                  ? "is-keyboard-focused"
                  : ""
              }`}
              onClick={onSelectChart}
            >
              <TrendingUp size={12} />
              <span>Chart View</span>
            </button>
            <button
              className={`jgs-btn-view view-metric ${
                active && activeViewKind === "Metric"
                  ? "is-keyboard-focused"
                  : ""
              }`}
              onClick={onSelectMetric}
            >
              <BarChart3 size={12} />
              <span>Metric View</span>
            </button>
          </div>
        ) : (
          <div className="jgs-item-badges">
            {item.viewKind && (
              <span
                className={`jgs-item-kind kind-${item.viewKind.toLowerCase()}`}
              >
                {item.viewKind}
              </span>
            )}
            {item.pageLabel && (
              <span className="jgs-item-page">
                <CornerUpRight size={11} />
                {item.pageLabel}
              </span>
            )}
          </div>
        )}
        <span className="jgs-item-enter">
          <CornerDownLeft size={13} />
        </span>
      </div>
    </div>
  );
}
